// use-case-details.ts
// This file provides hooks for fetching, updating, and managing case details, including authorization status and numbers, for a given case.
// Integrates with Athena/Cerner context and React Query for state management.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Procedure, CaseDetailsBody, UpdateAuthStatusArgs, GetAuthorizationResponse } from "@/types/procedure";
import { useAppState } from "@/contexts/app-state";
import { getCaseDetails, updateCaseDetails, updateAuthStatus, getAuthorization } from "@/services/caseDetails";

/**
 * useGetCaseDetails Hook
 * Fetches case details for a given case ID, using the current user and department context.
 *
 * @param caseId - The unique identifier for the case
 * @returns The query object containing data, status, and helpers from React Query
 *
 * @example
 * const { data, isLoading } = useGetCaseDetails("1234");
 */
export const useGetCaseDetails = (caseId: string) => {
  const { loggedInUser, context } = useAppState();

  return useQuery<Procedure, Error>({
    queryKey: ["case-details", { user: loggedInUser, caseId }],
    queryFn: () =>
      getCaseDetails({
        user: loggedInUser,
        departmentId: context?.athena?.departmentId || "ABM-123",
        practiceId: context?.athena?.practiceId || "8363",
        caseId,
      }),
  });
};

/**
 * useUpdateCaseDetails Hook
 * Provides a mutation for updating case details for a given case ID.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with mutation state and an updateCase function
 *
 * @example
 * const { updateCase, isPending } = useUpdateCaseDetails("1234");
 */
export const useUpdateCaseDetails = (caseId: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: updateCaseDetails,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["case-details", { user: loggedInUser, caseId }],
      });
    },
  });

  const updateCase = (body: CaseDetailsBody) => {
    mutate({
      user: loggedInUser,
      departmentId: context?.athena?.departmentId || "ABM-123",
      practiceId: context?.athena?.practiceId || "8363",
      caseId,
      body,
    });
  };

  return {
    isPending,
    isError,
    error,
    isSuccess,
    updateCase
  };
};

/**
 * useUpdateAuthStatus Hook
 * Provides a mutation for updating the authorization status of a case.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with mutation state and an updateStatus function
 *
 * @example
 * const { updateStatus, isPending } = useUpdateAuthStatus("1234");
 */
export const useUpdateAuthStatus = (caseId: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: updateAuthStatus,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["case-details", { user: loggedInUser, caseId }],
      });
      await queryClient.invalidateQueries({
        queryKey: ["orders"],
      });
    },
  });

  const updateStatus = (body: Omit<UpdateAuthStatusArgs["body"], "practiceId" | "caseNumber">) => {
    mutate({
      user: loggedInUser,
      departmentId: context?.athena?.departmentId || "ABM-123",
      requestorId: loggedInUser,
      requestorName: loggedInUser,
      requestorRole: loggedInUser,
      body: {
        practiceId: context?.athena?.practiceId || "8363",
        caseNumber: caseId,
        ...body,
      }
    });
  };

  return {
    isPending,
    isError,
    error,
    isSuccess,
    updateStatus,
  };
};

/**
 * useGetAuthorization Hook
 * Provides a mutation for requesting the latest authorization number for a case.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with mutation state, data, and a verifyAuthorization function
 *
 * @example
 * const { data, verifyAuthorization } = useGetAuthorization("1234");
 */
export const useGetAuthorization = (caseId: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();

  const { data, isPending, error, isError, isSuccess, mutate } = useMutation({
    mutationFn: getAuthorization,
    onSettled: async (data) => {
      // only invalidate the query if the authorization number is present in the response
      if (data?.authorizationNumber) {
        await queryClient.invalidateQueries({
          queryKey: ["case-details", { user: loggedInUser, caseId }],
        });
      }
    },
  });

  const verifyAuthorization = (onSettled?: (data: GetAuthorizationResponse | undefined) => Promise<void>) => {
    mutate({
      user: loggedInUser,
      departmentId: context?.athena?.departmentId || "ABM-123",
      practiceId: context?.athena?.practiceId || "8363",
      caseId,
    }, {
      onSettled: (data) => {
        if (onSettled) {
          onSettled(data).then(data => data).catch((e) => console.error(e));
        }
      }
    });
  };

  return {
    data,
    isPending,
    error,
    isError,
    isSuccess,
    verifyAuthorization,
  };
};

/**
 * useCaseDetails Hook
 * Combines all case details hooks into a single interface for a given case ID.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with get, put, patchAuthStatus, and authorization helpers
 *
 * @example
 * const { get, put, patchAuthStatus, authorization } = useCaseDetails("1234");
 * const { data, isLoading } = get;
 * // or
 * const { get: { data, isLoading }, put: { updateCase }, patchAuthStatus: { updateStatus }, authorization: { data, verifyAuthorization } } = useCaseDetails("1234");
 */
export const useCaseDetails = (caseId: string) => {
  return {
    get: useGetCaseDetails(caseId),
    put: useUpdateCaseDetails(caseId),
    patchAuthStatus: useUpdateAuthStatus(caseId),
    authorization: useGetAuthorization(caseId),
  };
};
